<template>
  <v-layout wrap>
    <v-flex xs12>
      <v-toolbar dense extended color="primary" dark style="padding-top:50px">
        <v-flex xs12 pa-5>
          <v-toolbar-title slot="extension" class="display-2">
            <v-icon x-large style="padding-bottom:10px">cloud_sync</v-icon>
            Continuous Data Ingestion Management
          </v-toolbar-title>
          Manage configurations, schedules, job runs, and photo uploads.
        </v-flex>
      </v-toolbar>
    </v-flex>

    <!-- Stats Cards -->
    <v-flex xs12 pa-3>
      <v-layout row wrap>
        <v-flex xs6 sm3 pa-1>
          <v-card color="primary" dark @click="$router.push({ name: 'ingestionConfigs' })" style="cursor:pointer">
            <v-card-text class="text-xs-center">
              <div class="display-1">{{ configCount }}</div>
              <div class="caption">Configurations</div>
            </v-card-text>
          </v-card>
        </v-flex>
        <v-flex xs6 sm3 pa-1>
          <v-card color="info" dark @click="$router.push({ name: 'ingestionSchedules' })" style="cursor:pointer">
            <v-card-text class="text-xs-center">
              <div class="display-1">{{ scheduleCount }}</div>
              <div class="caption">Schedules</div>
            </v-card-text>
          </v-card>
        </v-flex>
        <v-flex xs6 sm3 pa-1>
          <v-card color="success" dark @click="$router.push({ name: 'ingestionJobs', query: { from: todayISO } })" style="cursor:pointer">
            <v-card-text class="text-xs-center">
              <div class="display-1">{{ jobsToday }}</div>
              <div class="caption">Jobs Today</div>
            </v-card-text>
          </v-card>
        </v-flex>
        <v-flex xs6 sm3 pa-1>
          <v-card color="warning" dark @click="$router.push({ name: 'ingestionJobs' })" style="cursor:pointer">
            <v-card-text class="text-xs-center">
              <div class="display-1">{{ runningJobs }}</div>
              <div class="caption">Running / Queued</div>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>
    </v-flex>

    <!-- Quick Actions -->
    <v-flex xs12 pa-3>
      <v-layout row wrap>
        <v-flex xs12 sm6 md3 pa-1>
          <v-card hover @click="$router.push({ name: 'ingestionConfigs' })" style="cursor:pointer">
            <v-card-text class="text-xs-center">
              <v-icon large color="primary">settings</v-icon>
              <div class="subheading mt-2">Configurations</div>
              <div class="caption grey--text">Manage ingestion and photo configs</div>
            </v-card-text>
          </v-card>
        </v-flex>
        <v-flex xs12 sm6 md3 pa-1>
          <v-card hover @click="$router.push({ name: 'ingestionSchedules' })" style="cursor:pointer">
            <v-card-text class="text-xs-center">
              <v-icon large color="info">schedule</v-icon>
              <div class="subheading mt-2">Schedules</div>
              <div class="caption grey--text">Manage cron schedules and assignments</div>
            </v-card-text>
          </v-card>
        </v-flex>
        <v-flex xs12 sm6 md3 pa-1>
          <v-card hover @click="$router.push({ name: 'ingestionJobs' })" style="cursor:pointer">
            <v-card-text class="text-xs-center">
              <v-icon large color="success">history</v-icon>
              <div class="subheading mt-2">Job Runs</div>
              <div class="caption grey--text">View job history and results</div>
            </v-card-text>
          </v-card>
        </v-flex>
        <v-flex xs12 sm6 md3 pa-1>
          <v-card hover @click="$router.push({ name: 'ingestionPhotoUpload' })" style="cursor:pointer">
            <v-card-text class="text-xs-center">
              <v-icon large color="warning">add_a_photo</v-icon>
              <div class="subheading mt-2">Photo Upload</div>
              <div class="caption grey--text">Upload station photos</div>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>
    </v-flex>

    <!-- Manual Trigger -->
    <v-flex xs12 pa-3>
      <v-card>
        <v-card-text class="text-xs-center">
          <v-btn large color="primary" dark @click="triggerAll" :loading="triggering">
            <v-icon left>play_arrow</v-icon>Trigger All Active Configurations
          </v-btn>
          <div v-if="triggerResult" class="mt-2">
            <v-chip color="success" dark>Batch: {{ triggerResult.BatchId }}</v-chip>
            <v-chip color="info" dark>{{ triggerResult.JobRunIds.length }} jobs created</v-chip>
          </div>
        </v-card-text>
      </v-card>
    </v-flex>

    <!-- Recent Job Runs -->
    <v-flex xs12 pa-3>
      <v-card>
        <v-card-title>
          <h3>Recent Job Runs</h3>
          <v-spacer></v-spacer>
          <v-btn small color="info" :to="{ name: 'ingestionJobs' }">View All</v-btn>
        </v-card-title>
        <v-data-table :headers="jobHeaders" :items="recentJobs" :loading="jobsLoading" hide-actions>
          <template slot="items" slot-scope="props">
            <td style="max-width:120px;overflow:hidden;text-overflow:ellipsis" :title="props.item.Id">
              <router-link :to="`/administration/ingestion-jobs/${props.item.Id}`">
                {{ shorten(props.item.Id) }}
              </router-link>
            </td>
            <td>
              <router-link v-if="props.item.ConfigurationId"
                :to="{ name: 'ingestionConfigs' }">
                {{ configName(props.item.ConfigurationId) }}
              </router-link>
              <span v-else>-</span>
            </td>
            <td>
              <v-chip :color="statusColor(props.item.Status)" small dark>{{ props.item.Status }}</v-chip>
            </td>
            <td>{{ formatDate(props.item.StartedAt) }}</td>
            <td>{{ formatDuration(props.item.DurationSeconds) }}</td>
            <td class="text-xs-right">
              <v-btn small color="info" depressed :to="`/administration/ingestion-jobs/${props.item.Id}`">View</v-btn>
            </td>
          </template>
        </v-data-table>
      </v-card>
    </v-flex>

    <v-snackbar :color="snackColor" :timeout="6000" top v-model="snackbar">
      {{ snackMessage }}
      <v-btn dark flat @click="snackbar = false">Close</v-btn>
    </v-snackbar>
  </v-layout>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
const ingestion = createNamespacedHelpers('ingestionManagement')

export default {
  data() {
    return {
      triggering: false,
      triggerResult: null,
      jobsLoading: false,
      snackbar: false, snackColor: 'error', snackMessage: '',
      jobHeaders: [
        { text: 'Job ID', value: 'Id', align: 'left', sortable: false },
        { text: 'Config', value: 'ConfigurationId', align: 'left', sortable: false },
        { text: 'Status', value: 'Status', align: 'left', sortable: false },
        { text: 'Started', value: 'StartedAt', align: 'left', sortable: false },
        { text: 'Duration', value: 'DurationSeconds', align: 'left', sortable: false },
        { text: '', value: null, align: 'right', sortable: false },
      ],
    }
  },
  computed: {
    ...ingestion.mapGetters(['configurations', 'schedules', 'jobs']),
    configCount() { return this.configurations.length },
    scheduleCount() { return this.schedules.length },
    recentJobs() { return this.jobs.slice(0, 10) },
    jobsToday() {
      const today = new Date().toDateString()
      return this.jobs.filter(j => new Date(j.StartedAt).toDateString() === today).length
    },
    runningJobs() {
      return this.jobs.filter(j => j.Status === 'Running' || j.Status === 'Queued').length
    },
    todayISO() {
      const d = new Date()
      const yyyy = d.getFullYear()
      const mm = String(d.getMonth() + 1).padStart(2, '0')
      const dd = String(d.getDate()).padStart(2, '0')
      return `${yyyy}-${mm}-${dd}`
    },
  },
  methods: {
    ...ingestion.mapActions(['fetchConfigurations', 'fetchSchedules', 'fetchJobRuns', 'triggerIngestion']),
    statusColor(s) {
      return { Queued: 'grey', Running: 'info', Completed: 'success', Failed: 'error' }[s] || 'grey'
    },
    formatDate(d) { return d ? new Date(d).toLocaleString() : '' },
    formatDuration(sec) {
      if (sec == null) return '-'
      const m = Math.floor(sec / 60); const s = Math.floor(sec % 60)
      return m > 0 ? `${m}m ${s}s` : `${s}s`
    },
    shorten(id) { return id ? id.substring(0, 8) + '...' : '' },
    configName(configId) {
      if (!configId) return '-'
      const cfg = this.configurations.find(c => c.Id === configId)
      return cfg ? cfg.Name : `#${configId}`
    },
    triggerAll() {
      this.triggering = true; this.triggerResult = null
      this.triggerIngestion({
        successCb: (data) => { this.triggerResult = data; this.triggering = false; this.loadRecentJobs() },
        failedCb: (err) => {
          this.snackMessage = err.response ? (err.response.data || err.response.statusText) : err.message
          this.snackColor = 'error'; this.snackbar = true; this.triggering = false
        },
      })
    },
    loadData() {
      this.fetchConfigurations()
      this.fetchSchedules()
      this.loadRecentJobs()
    },
    loadRecentJobs() {
      this.jobsLoading = true
      this.fetchJobRuns({ params: { page: 1, pageSize: 10 } }).finally(() => { this.jobsLoading = false })
    },
  },
  mounted() { this.loadData() },
}
</script>
